/**
 * ConformidadCliente — Hoja de conformidad del cliente para imprimir.
 * Copia fiel del documento físico usado por Taller del Este.
 *
 * Uso:
 *   <ConformidadCliente data={conformidadData} onClose={() => setData(null)} />
 *
 * Al montar, llama a window.print(). El overlay es invisible en pantalla
 * pero al imprimir sólo se imprime este componente (ver CSS @media print en index.css).
 */
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Printer } from "lucide-react";
import { Button } from "@/components/ui";
import { API_BASE } from "@/lib/api";

function fmt(val) {
  return val || "—";
}

function fmtFecha(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("es-PY", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function ConformidadCliente({ data, onClose, autoPrint = false }) {
  const portalEl = useRef(null);
  if (!portalEl.current) {
    portalEl.current = document.createElement("div");
    portalEl.current.className = "conformidad-portal";
  }

  useEffect(() => {
    document.body.appendChild(portalEl.current);
    return () => {
      if (portalEl.current && document.body.contains(portalEl.current)) {
        document.body.removeChild(portalEl.current);
      }
    };
  }, []);

  useEffect(() => {
    if (autoPrint) {
      const t = setTimeout(() => {
        const prev = document.title;
        document.title = `Hoja Conformidad - ${data.presupuesto_numero || data.ot_numero || "OT"}`;
        window.print();
        setTimeout(() => { document.title = prev; }, 500);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [autoPrint, data]);

  if (!data) return null;

  const emp = data.empresa || {};

  function handlePrint() {
    const prev = document.title;
    document.title = `Hoja Conformidad - ${data.presupuesto_numero || data.ot_numero || "OT"}`;
    window.print();
    setTimeout(() => { document.title = prev; }, 500);
  }

  const content = (
    <>
      {/* Overlay de pantalla — oculto al imprimir */}
      <div
        className="conformidad-screen fixed inset-0 z-50 flex items-start justify-center
                   bg-black/60 overflow-y-auto py-8 px-4"
      >
        {/* Controles UI — nunca impresos */}
        <div className="no-print flex gap-2 fixed top-4 right-4 z-60">
          <Button size="sm" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-1" /> Imprimir
          </Button>
          <Button size="sm" variant="outline" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Hoja A4 */}
        <div
          className="conformidad-sheet bg-white text-black w-[210mm] min-h-[297mm]
                     shadow-2xl p-[18mm] font-serif text-[11pt] leading-relaxed"
          style={{ fontFamily: "'Times New Roman', Times, serif" }}
        >
          {/* ── Encabezado ── */}
          <div className="flex items-start justify-between border-b-2 border-black pb-3 mb-4">
            {/* Logo izquierda */}
            <div className="flex flex-col items-start shrink-0">
              {emp.logo_url ? (
                <img
                  src={`${API_BASE.replace(/\/api\/?$/, "")}${emp.logo_url}`}
                  alt="Logo"
                  style={{ maxWidth: "90px", maxHeight: "70px", objectFit: "contain" }}
                />
              ) : (
                <div className="border border-black px-3 py-1 text-center leading-tight">
                  <div className="font-bold text-[10pt] tracking-widest">TALLER</div>
                  <div className="font-bold text-[10pt] tracking-widest">DEL ESTE</div>
                </div>
              )}
            </div>
            {/* Info empresa centro */}
            <div className="flex-1 text-center px-4">
              <p className="font-bold text-[13pt]">{emp.nombre || "TALLER DEL ESTE"}</p>
              <p className="text-[9pt]">{emp.label || "Taller de reparación de automóviles - Estética Automotriz"}</p>
              <p className="text-[9pt]">{emp.direccion || "Av. Bicentenario y Sucre, Km 5/2"}</p>
              <p className="text-[9pt]">Chapería y Pintura</p>
            </div>
            {/* Espacio derecha */}
            <div className="w-24" />
          </div>

          {/* ── Título ── */}
          <div className="text-center my-4">
            <p className="font-bold underline text-[12pt] tracking-wide">
              CONFORMIDAD DEL CLIENTE
            </p>
          </div>

          {/* ── Tabla de datos principales ── */}
          <table className="w-full border-collapse text-[10pt] mb-4">
            <tbody>
              <tr>
                <td className="border border-black px-2 py-1 font-semibold w-[30%]">Aseguradora:</td>
                <td className="border border-black px-2 py-1 w-[40%]">{fmt(data.aseguradora_nombre)}</td>
                <td className="border border-black px-2 py-1 font-semibold w-[15%]">Fecha:</td>
                <td className="border border-black px-2 py-1 w-[15%]">&nbsp;</td>
              </tr>
              <tr>
                <td className="border border-black px-2 py-1 font-semibold">Propietario:</td>
                <td className="border border-black px-2 py-1">{fmt(data.cliente_nombre)}</td>
                <td className="border border-black px-2 py-1 font-semibold">PolizaNro:</td>
                <td className="border border-black px-2 py-1">{fmt(data.numero_poliza)}</td>
              </tr>
              <tr>
                <td className="border border-black px-2 py-1 font-semibold">Marca:</td>
                <td className="border border-black px-2 py-1">
                  {fmt(data.vehiculo_marca)} {data.vehiculo_modelo ? `Modelo: ${data.vehiculo_modelo}` : ""}{" "}
                  {data.vehiculo_color ? `Color: ${data.vehiculo_color}` : ""}
                </td>
                <td className="border border-black px-2 py-1 font-semibold">Chapa:</td>
                <td className="border border-black px-2 py-1">{fmt(data.vehiculo_patente)}</td>
              </tr>
              <tr>
                <td className="border border-black px-2 py-1 font-semibold">Chasis:</td>
                <td className="border border-black px-2 py-1">{fmt(data.vehiculo_chasis)}</td>
                <td className="border border-black px-2 py-1 font-semibold">Agente:</td>
                <td className="border border-black px-2 py-1">{fmt(data.agente)}</td>
              </tr>
              <tr>
                <td className="border border-black px-2 py-1 font-semibold">Teléfono:</td>
                <td className="border border-black px-2 py-1">{fmt(data.cliente_telefono)}</td>
                <td className="border border-black px-2 py-1 font-semibold">Siniestro:</td>
                <td className="border border-black px-2 py-1">{fmt(data.numero_siniestro)}</td>
              </tr>
              <tr>
                <td className="border border-black px-2 py-1 font-semibold">N° Orden Provisión:</td>
                <td className="border border-black px-2 py-1" colSpan={3}>{fmt(data.numero_provision)}</td>
              </tr>
            </tbody>
          </table>

          {/* ── Texto de conformidad ── */}
          <div className="my-5 text-[10.5pt] leading-loose">
            <p>
              Por la presente comunico mi total conformidad por el servicio de{" "}
              <strong>CHAPERIA Y PINTURA</strong>, realizado por el TALLER{" "}
              <strong>DEL ESTE E.A.S.</strong>{" "}
              <em>Centro de Estetica Automotiva.-</em>
            </p>
          </div>

          {/* ── Detalles del automóvil ── */}
          <div className="my-5">
            <p className="font-bold underline text-center text-[11pt] mb-3">
              DETALLES DEL AUTOMOVIL
            </p>
            <div className="pl-8 space-y-1.5 text-[10.5pt]">
              {[
                ["MARCA",       fmt(data.vehiculo_marca)],
                ["MODELO",      fmt(data.vehiculo_modelo)],
                ["COLOR",       fmt(data.vehiculo_color)],
                ["CHAPA",       fmt(data.vehiculo_patente)],
                ["PRESUPUESTO", data.presupuesto_numero || "A/D"],
              ].map(([label, value]) => (
                <div key={label} className="flex items-baseline gap-2">
                  <span className="w-[6rem] font-bold">• {label}</span>
                  <span>: {value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Cierre ── */}
          <div className="mt-6 text-[10.5pt]">
            <p>
              Sin otro particular, agradezco las atenciones brindadas a mi persona,
              atentamente.
            </p>
          </div>

          {/* ── Firma ── */}
          <div className="mt-8 space-y-5 text-[10.5pt]">
            {[
              "FIRMA",
              "ACLARACION",
              "N° DE CEDULA",
              "NRO DE CONTACTO",
            ].map((label) => (
              <div key={label} className="flex items-end gap-2">
                <span className="font-bold whitespace-nowrap w-44">{label}</span>
                <span className="flex-1 border-b border-black" style={{ minWidth: "200px" }} />
              </div>
            ))}
          </div>

          {/* ── Footer ── */}
          <div className="mt-12 text-center text-[9pt] border-t border-black pt-3">
            <p>{emp.direccion || "Av. Bicentenario c/sucre km 5/2 barrio – cuidad del este – py"}</p>
            <p>Tel:{emp.telefono || "0973-779-893"}</p>
            <p>Correo: {emp.email || "tdeste2024@gmail.com"}</p>
            <p>Ruc: {emp.ruc || "80145497-2"}</p>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(content, portalEl.current);
}
